import { formatDate } from '@seen/core/dates'
import type { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { Badge, Button } from '@seen/ui'
import { forwardRef, type ReactNode } from 'react'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import type { Field } from '#src/components/core/form/FormField.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { IconDue } from '#src/components/icons/IconDue.tsx'
import { IconTaskState } from '#src/components/icons/IconTaskState.tsx'
import { IconUserPlaceholder } from '#src/components/icons/IconUserPlaceholder.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { RichText } from '#src/components/RichText.tsx'
import { TaskPriorityCombobox } from '#src/components/task/TaskPriorityCombobox.tsx'
import { TaskPriorityIcon } from '#src/components/task/TaskPriorityIcon.tsx'
import { TaskStateCombobox } from '#src/components/task/TaskStateCombobox.tsx'
import { useOwnerComboboxOptions } from '#src/components/UseOwnerComboboxOptions.tsx'

interface OrderNextTaskDisplayProps {
  task: StandardTask
  rowWidth: number
}

const TITLE_DISPLAY_THRESHOLD = 825

export function OrderNextTaskDisplay({
  task,
  rowWidth,
}: OrderNextTaskDisplayProps): ReactNode {
  const shouldShowTitle = rowWidth >= TITLE_DISPLAY_THRESHOLD

  const rowContent = (
    <div className="flex items-center gap-1 rounded-lg border bg-muted/30 px-1 py-1">
      <ResourceFieldForm
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        instance={task}
        valueFn={(t) => t.state}
        patchFn={(t, value) => {
          t.state = value
        }}
        className="self-center"
        render={({ field: { value, onChange, ...rest } }) => (
          <TaskStateCombobox onSelect={onChange} selected={value}>
            <Button
              variant="unstyled"
              className="flex h-6 w-4 items-center justify-center p-0 align-middle hover:brightness-hover-icon data-[state=open]:brightness-hover-icon"
              {...rest}
            >
              <IconTaskState state={value} className="size-4" />
            </Button>
          </TaskStateCombobox>
        )}
      />
      <ResourceFieldForm
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        instance={task}
        valueFn={(t) => t.priority ?? null}
        patchFn={(t, value) => {
          t.priority = value ?? undefined
        }}
        render={({ field: { value, onChange, ...rest } }) => (
          <TaskPriorityCombobox onSelect={onChange} selected={value}>
            <Button
              variant="unstyled"
              className="flex h-6 w-4 items-center justify-center p-0 align-middle hover:brightness-hover-icon data-[state=open]:brightness-hover-icon"
              {...rest}
            >
              <TaskPriorityIcon
                priority={value ?? undefined}
                isTaskCompletedOrCancelled={task.isCompletedOrCancelled}
                className="size-4"
              />
            </Button>
          </TaskPriorityCombobox>
        )}
      />
      <ResourceFieldForm
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        instance={task}
        valueFn={(task) => task.ownerPractitioner?.reference ?? null}
        patchFn={(task, value) => {
          task.ownerPractitioner = value ? { reference: value } : undefined
        }}
        render={({ field }) => <OwnerIconCombobox {...field} />}
      />
      <ResourceFieldForm
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        instance={task}
        valueFn={(t) => t.dueOn ?? null}
        patchFn={(t, value) => {
          t.dueOn = value ?? undefined
        }}
        render={({ field }) => (
          <DatePicker
            {...field}
            variant="outline"
            className="h-auto p-0.5 align-middle text-3xs"
            hideIcon
            asChild
            allowUndefined
          >
            <Badge
              variant="outline"
              className="flex h-6 items-center justify-center gap-0.5 align-middle"
            >
              {task.dueOn ? (
                <>
                  <IconDue
                    dateString={task.dueOn}
                    isCompleted={task.isCompletedOrCancelled}
                    className="size-3 align-middle"
                  />
                  <span className="align-middle">{formatDate(task.dueOn)}</span>
                </>
              ) : (
                <span className="align-middle text-foreground-secondary">
                  Due date
                </span>
              )}
            </Badge>
          </DatePicker>
        )}
      />
      {shouldShowTitle && (
        <RichText
          className="line-clamp-1 max-w-[125px] self-center align-middle text-2xs break-all text-foreground-secondary"
          value={task.title}
          inline
        />
      )}
    </div>
  )

  if (shouldShowTitle) {
    return rowContent
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{rowContent}</TooltipTrigger>
      <TooltipContent>
        <span className="text-xs wrap-break-word">{task.title}</span>
      </TooltipContent>
    </Tooltip>
  )
}

const OwnerIconCombobox = forwardRef<HTMLButtonElement, Field<string | null>>(
  ({ value, onChange, ...fieldRest }, ref) => {
    const options = useOwnerComboboxOptions(value)
    return (
      <Combobox options={options} onSelect={onChange}>
        <Button
          variant="unstyled"
          className="flex h-6 w-4 items-center justify-center p-0 align-middle hover:brightness-hover-icon data-[state=open]:brightness-hover-icon"
          ref={ref}
          {...fieldRest}
        >
          {value && <Avatar profile={{ reference: value }} size={3.5} />}
          {!value && (
            <IconUserPlaceholder className="size-3.5 text-foreground-secondary" />
          )}
        </Button>
      </Combobox>
    )
  }
)
OwnerIconCombobox.displayName = 'OwnerIconCombobox'
